import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
    type ReactNode,
} from "react";
import * as msal from "@azure/msal-browser";
import {
    msalConfig,
    redirectTokenRequest,
    silentTokenRequest,
    loginRedirectRequest,
    PREFERRED_LOGIN_METHOD_KEY,
    LOGIN_METHODS,
    WORKNEST_TOKEN_KEY,
    REDIRECT_URL_KEY,
} from "../constants/authConfig";
import WorknestTokenError from "../errors/worknestTokenError";
import tokenService from "../services/tokenService";
import { setAuthHeader } from "../plugins/axios";
import { jwtDecode } from "jwt-decode";
import LoginLoader from "../components/LoginLoader";
import { useLocation } from "react-router-dom";
import { differenceInHours } from "date-fns";

interface WorkNestTokenResponse {
    token: string;
    expires: string;
}

interface UserDetails {
    email: string;
    firstName: string;
    lastName: string;
    authMethod: string;
    userId: string;
    roles: string[];
    tid?: string;
    tenant?: string;
}

interface User {
    details: UserDetails;
    token: string;
    expiresOn: string;
}

interface AuthContextValue {
    loginViaMicrosoft: () => Promise<void>;
    isLoading: boolean;
    user: User | null;
    logout: () => Promise<void>;
    hasRole: (role: string) => boolean;
    ignoreBrowserBehaviour: boolean;
    setIgnoreBrowserBehaviour: (v: boolean) => void;
    onGoogleLoginSuccess: (googleTokenResponse: string) => Promise<void>;
    handleSessionInit: () => Promise<void>;
}

const msalInstance = new msal.PublicClientApplication(msalConfig);
// MSAL v4 requires initialize() before any API call.
const msalReady = msalInstance.initialize();

const MsalContext = createContext<AuthContextValue | undefined>(undefined);

const setPreferredLoginMethod = (preferredLoginMethod: string) =>
    localStorage.setItem(PREFERRED_LOGIN_METHOD_KEY, preferredLoginMethod);
const getPreferredLoginMethod = () => localStorage.getItem(PREFERRED_LOGIN_METHOD_KEY);
const setWorkNestToken = (workNestToken: WorkNestTokenResponse) =>
    localStorage.setItem(WORKNEST_TOKEN_KEY, JSON.stringify(workNestToken));
const getWorkNestToken = (): WorkNestTokenResponse | null => {
    const raw = localStorage.getItem(WORKNEST_TOKEN_KEY);
    return raw ? JSON.parse(raw) : null;
};
const removeWorkNestToken = () => localStorage.removeItem(WORKNEST_TOKEN_KEY);

const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [ignoreBrowserBehaviour, setIgnoreBrowserBehaviour] = useState(false);
    const location = useLocation();

    useEffect(() => {
        if (!localStorage.getItem(REDIRECT_URL_KEY) && location.pathname !== "/")
            localStorage.setItem(REDIRECT_URL_KEY, location.pathname);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const initUserDetails = useCallback((workNestTokenResponse: WorkNestTokenResponse) => {
        const decoded: any = jwtDecode(workNestTokenResponse.token);
        const details: UserDetails = {
            email: decoded.email,
            firstName: decoded.given_name,
            lastName: decoded.family_name,
            authMethod: decoded.authentication_method,
            userId: decoded.sub,
            roles: Array.isArray(decoded.roles) ? decoded.roles : [decoded.roles],
            tid: decoded.tid,
            tenant: decoded.tenant,
        };

        const userObject: User = {
            details,
            token: workNestTokenResponse.token,
            expiresOn: workNestTokenResponse.expires,
        };
        setUser(userObject);
        setAuthHeader(workNestTokenResponse.token);

        return details;
    }, []);

    const initialiseWorknestUserSession = useCallback(
        (workNestTokenResponse: WorkNestTokenResponse) =>
            new Promise<void>((resolve, reject) => {
                try {
                    setWorkNestToken(workNestTokenResponse);
                    initUserDetails(workNestTokenResponse);
                    resolve();
                } catch (e) {
                    reject(e);
                } finally {
                    setIsLoading(false);
                }
            }),
        [initUserDetails],
    );

    const handleMicrosoftTokenResponse = useCallback(
        async (azureToken: any) => {
            try {
                const worknestTokenResponse = await tokenService.microsoftWorkNestTokenExchange(
                    azureToken.idToken,
                );
                setWorkNestToken(worknestTokenResponse as WorkNestTokenResponse);
                initUserDetails(worknestTokenResponse as WorkNestTokenResponse);
                setIsLoading(false);
            } catch (error) {
                setIsLoading(false);
                throw error;
            }
        },
        [initUserDetails],
    );

    const handleError = useCallback(
        async (error: unknown) => {
            setIsLoading(false);

            if (error instanceof WorknestTokenError) {
                console.error(error.message);
                return;
            }

            if (error instanceof msal.InteractionRequiredAuthError) {
                try {
                    const redirectTokenResponse = await msalInstance.acquireTokenRedirect(
                        redirectTokenRequest,
                    );
                    await handleMicrosoftTokenResponse(redirectTokenResponse);
                } catch (err) {
                    console.error(err);
                }
            }
        },
        [handleMicrosoftTokenResponse],
    );

    const loginViaMicrosoft = async () => {
        setPreferredLoginMethod(LOGIN_METHODS.microsoft);
        await msalInstance.loginRedirect(loginRedirectRequest);
    };

    const initMicrosoftAuth = useCallback(async () => {
        await msalReady;
        const token = await msalInstance.handleRedirectPromise();

        if (token) {
            await handleMicrosoftTokenResponse(token);
            return;
        }

        const accounts = msalInstance.getAllAccounts();
        if (!accounts.length) {
            setIsLoading(false);
        } else if (accounts.length === 1) {
            const account = accounts[0];
            const silentTokenResponse = await msalInstance.acquireTokenSilent({
                ...silentTokenRequest,
                account,
            });
            await handleMicrosoftTokenResponse(silentTokenResponse);
        } else {
            return await loginViaMicrosoft();
        }
    }, [handleMicrosoftTokenResponse]);

    const handleSessionInit = async () => {
        try {
            const workNestTokenFromStorage = getWorkNestToken();

            if (workNestTokenFromStorage) {
                const decoded: any = jwtDecode(workNestTokenFromStorage.token);
                const hoursToExpiry = differenceInHours(
                    new Date(decoded.exp * 1000),
                    new Date(),
                );
                if (hoursToExpiry >= 3) {
                    await initialiseWorknestUserSession(workNestTokenFromStorage);
                    return;
                }
            }

            const preferredLogin = getPreferredLoginMethod();

            if (preferredLogin === LOGIN_METHODS.microsoft) {
                await initMicrosoftAuth();
            } else {
                setIsLoading(false);
            }
        } catch (error) {
            handleError(error);
        }
    };

    useEffect(() => {
        handleSessionInit();
    }, [handleError, initMicrosoftAuth, initialiseWorknestUserSession]);

    const onGoogleLoginSuccess = async (googleTokenResponse: string) => {
        try {
            setPreferredLoginMethod(LOGIN_METHODS.google);
            const worknestTokenResponse = await tokenService.googleWorkNestTokenExchange(
                googleTokenResponse,
            );
            await initialiseWorknestUserSession(worknestTokenResponse as WorkNestTokenResponse);
        } catch (error) {
            handleError(error);
        }
    };

    const logout = async () => {
        removeWorkNestToken();
        const authMethod = user?.details.authMethod;
        if (authMethod === LOGIN_METHODS.microsoft) {
            await msalInstance.logoutRedirect();
        } else if (authMethod === LOGIN_METHODS.google) {
            setUser(null);
            window.google?.accounts.id.disableAutoSelect();
        }
    };

    const hasRole = (role: string) =>
        // A role containing a namespace (e.g. "client:consultant") is matched as-is;
        // a bare role (e.g. "Targets") is treated as a tools: role.
        !!user?.details.roles &&
        user.details.roles.includes(role.includes(":") ? role : `tools:${role.toLowerCase()}`);

    if (isLoading) return <LoginLoader />;

    const contextValue: AuthContextValue = {
        loginViaMicrosoft,
        isLoading,
        user,
        logout,
        hasRole,
        ignoreBrowserBehaviour,
        setIgnoreBrowserBehaviour,
        onGoogleLoginSuccess,
        handleSessionInit,
    };

    return <MsalContext.Provider value={contextValue}>{[children]}</MsalContext.Provider>;
};

const useAuth = (): AuthContextValue => {
    const ctx = useContext(MsalContext);
    if (!ctx) throw new Error("useAuth must be used inside an AuthProvider");
    return ctx;
};

export { AuthProvider, useAuth, MsalContext };
